import * as React from 'react';
import { useSelector } from 'react-redux';
import { useAtomValue } from 'jotai';
import { Divider, Space } from 'antd';
import formatGaugeRingData from '@utils/overview/gaugeRing';

import { currentMetricsMetaSelector } from '@src/store/common/selectors';
import {
  BLUE_COLOR,
  RED_COLOR,
  GOOD_SESSIONS_NUMBER_COLOR,
  SPI_QUERY_METRICS,
  DEFAULT_METRIC,
} from '../../utils/spi';
import {
  GoodSessionsWidgetWrapper,
  ImpactedSessionsWidgetWrapper,
  WidgetTextWrapper,
  WidgetTitle,
  WidgetSubscriptWrapper,
  SessionsWrapper,
  StreamNumberWrapper,
} from './styled';
import { SESSION_WIDGET_CONFIG } from '@utils/kpi/spi';
import { useWidget } from '../../state/dashboard';
import { useCurrentKpiConfig } from '@src/hooks/useCurrentKpiConfig';
import useFetchSPIData from './useFetchSPIData';
import RemoveWidget from '../../control/RemoveWidget';
import Drilldown from '../../control/Drilldown';
import { Retry } from '@src/components/common';
import useDiagDashboard from '../../hooks/useDiagDashboard';
import BaseChart, { useChartInstance } from '@src/components/common/BaseChart';
import WidgetCardWrapper from '../../common/WidgetCardWrapper';
import useSelectMetric from '../../hooks/useSelectMetric';
import { getHumanizedValueString } from '../../utils/format';

const StreamsWidget = ({ wid }) => {
  const widgetHandler = useWidget({ wid });
  const currentKpi = useCurrentKpiConfig();

  const { isFetching, isError, data, refetch, queued } = useFetchSPIData({
    wid,
    metrics: SPI_QUERY_METRICS,
    currentKpi,
  });
  const totalsData = data?.totals;

  const { createDashboard } = useDiagDashboard({
    metrics: SPI_QUERY_METRICS,
    defaultMetric: DEFAULT_METRIC.streams,
    title: widgetHandler.meta.title,
  });

  return (
    <WidgetCardWrapper
      wid={wid}
      title={widgetHandler.meta.title}
      style={{ gridRow: 'span 7' }}
      extra={
        <Space>
          <Drilldown createDashboard={createDashboard} />
          <RemoveWidget onClick={widgetHandler.remove} />
        </Space>
      }
    >
      <Retry
        isLoading={isFetching}
        showError={isError}
        fetchData={refetch}
        progress={true}
        queued={queued}
      >
        <div>
          <Sessions data={totalsData} />
        </div>
      </Retry>
    </WidgetCardWrapper>
  );
};

const Sessions = ({ data = {} }) => {
  const { instance, chartInstanceRefCb } = useChartInstance({});
  const currentMetricsMeta = useSelector(currentMetricsMetaSelector);
  const badSessionsMetric = SESSION_WIDGET_CONFIG.bad;
  const goodSessionsValue = getHumanizedValueString(data, SESSION_WIDGET_CONFIG.good);
  const badSessionsValue = getHumanizedValueString(data, badSessionsMetric);
  const badSessionsPctValue = data[SESSION_WIDGET_CONFIG.badRate];
  const goodSessionsAvgPlayDuration = getHumanizedValueString(
    data,
    SESSION_WIDGET_CONFIG.goodSecondary,
  );
  const badSessionsAvgPlayDuration = getHumanizedValueString(
    data,
    SESSION_WIDGET_CONFIG.badSecondary,
  );

  const chartOption = React.useMemo(() => {
    const chartOption = formatGaugeRingData(
      [
        { value: 100 - badSessionsPctValue, name: 'Good Streams' },
        { value: badSessionsPctValue, name: 'Impacted Streams' },
      ],
      [BLUE_COLOR, RED_COLOR],
    );

    return chartOption ? chartOption : {};
  }, [badSessionsPctValue]);

  React.useEffect(() => {
    if (instance) {
      instance.setOption(chartOption);
    }
  }, [instance, chartOption]);

  const selectMetric = useSelectMetric();

  const handleClick = () => {
    selectMetric(badSessionsMetric);
  };

  return (
    <SessionsWrapper>
      <BaseChart chartInstanceRefCb={chartInstanceRefCb} style={{ height: 80, width: 80 }} />
      <GoodSessionsWidgetWrapper>
        <WidgetTextWrapper>
          <WidgetTitle>{currentMetricsMeta[SESSION_WIDGET_CONFIG.good].displayName}</WidgetTitle>
          <StreamNumberWrapper style={{ color: GOOD_SESSIONS_NUMBER_COLOR, display: 'flex' }}>
            <span style={{ fontSize: 16 }}>{goodSessionsValue}</span>
            {SESSION_WIDGET_CONFIG.goodSecondary && (
              <WidgetSubscriptWrapper>
                <span>{goodSessionsAvgPlayDuration}</span> {'Avg. Playing Time'}
              </WidgetSubscriptWrapper>
            )}
          </StreamNumberWrapper>
        </WidgetTextWrapper>
      </GoodSessionsWidgetWrapper>
      <Divider type="vertical" style={{ height: 60 }} />
      <ImpactedSessionsWidgetWrapper onClick={handleClick}>
        <WidgetTextWrapper>
          <WidgetTitle>{currentMetricsMeta[badSessionsMetric].displayName}</WidgetTitle>
          <StreamNumberWrapper style={{ color: RED_COLOR, display: 'flex' }}>
            <span style={{ fontSize: 16 }}>{badSessionsValue}</span>
            {SESSION_WIDGET_CONFIG.badSecondary && (
              <WidgetSubscriptWrapper>
                <span>{badSessionsAvgPlayDuration}</span> {'Avg. Playing Time'}
              </WidgetSubscriptWrapper>
            )}
          </StreamNumberWrapper>
        </WidgetTextWrapper>
      </ImpactedSessionsWidgetWrapper>
    </SessionsWrapper>
  );
};

export default StreamsWidget;
